import { Card, Box } from "@mui/material";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import PropTypes from "prop-types";

import SoftBox from "components/SoftComponents/SoftBox";
import Divider from "@material-ui/core/Divider";
import SoftTypography from "components/SoftComponents/SoftTypography";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";
import { useSoftUIController } from "hooks/context";
import { getConnectivityIcon, getDeviceTypeIcon } from "utils/device";
import moment from "moment";

function NamingConventions(device_name) {

    var [header, subheader, triheader] = [device_name, undefined, undefined];

    if (device_name.includes("_")) {
        [header, subheader, triheader] = device_name.split("_").map((el) => el.trim());
    }
    else if (device_name.includes(" opp ")) {
        [header, subheader, triheader] = device_name.split(" opp ").map((el) => el.trim());
        subheader = "opp. " + subheader;
    }
    else if (device_name.includes(" at ")) {
        [header, subheader, triheader] = device_name.split(" at ").map((el) => el.trim());
        subheader = "at " + subheader;
    }
    else if (device_name.includes(" before ")) {
        [header, subheader, triheader] = device_name.split(" before ").map((el) => el.trim());
        subheader = "before " + subheader;
    }
    else if (device_name.includes(" after ")) {
        [header, subheader, triheader] = device_name.split(" after ").map((el) => el.trim());
        subheader = "after " + subheader;
    }
    else if (device_name.includes(" near ")) {
        [header, subheader, triheader] = device_name.split(" near ").map((el) => el.trim());
        subheader = "near " + subheader;
    }
    else if (device_name.includes(",")) {
        [header, subheader, triheader] = device_name.split(",").map((el) => el.trim());
    }

    return [header, subheader, triheader];
}

function DeviceOverviewTab({ device }) {

    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf("devices-advanced:view") >= 0;

    const [header, subheader, triheader] = NamingConventions(device.name);
    const showStats = (typeof device?.temperature != 'undefined' && typeof device?.battery != 'undefined' && typeof device?.rssi != 'undefined' && typeof device?.humidity != 'undefined') ? true : false;

    var rel_time = device?.last_heartbeat ? Math.floor((moment().unix() - device?.last_heartbeat)/60) : null;
    const device_liveview_last_update = typeof rel_time != null ? (rel_time < 1 ? 'Less than a minute ago' : (rel_time == 1 ? `1 minute ago` : `${rel_time} minutes ago`)) : device.last_updated;

    const overviewInfo = (
        <Card sx={{ height: "100%", overflowY: 'auto', background: '#f9f9f9', borderRadius: '12px' }} >
            <SoftBox py={2} px={2}>
                <SoftBox mt={1} mx={1} display="flex" flexDirection="row" flexWrap="wrap" alignItems="baseline" sx={{ gap: 0.5 }}>
                    {device.organisation_id == 'clearways' ? (
                        <>
                            <SoftTypography variant={deviceLayout == "mobile" ? "h3" : "h2"} fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{`eMinor ${header}`}</SoftTypography>
                            <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{typeof device?.stop?.site_id == 'undefined' ? `Site ID Not Set` : `(${device?.stop?.site_id})`}</SoftTypography>
                        </>
                    ) : (
                        <>
                            <SoftTypography variant={deviceLayout == "mobile" ? "h3" : "h2"} fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                            {subheader && <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}{triheader && ', '}</SoftTypography>}
                            {triheader && <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ color: "#777", verticalAlign: 'bottom', lineHeight: 1.5 }}>{triheader}</SoftTypography>}
                        </>
                    )}
                </SoftBox>
                <SoftBox my={2}>
                    <Divider />
                </SoftBox>
                <Card>
                    <SoftBox mx={2} my={1}>
                        <HealthStatusCard statistics={device} showAdvancedDeviceLevel={showAdvancedDeviceLevel} transparent={true} />
                        {showStats && (
                            <>
                                <SoftBox my={1}>
                                    <Divider />
                                </SoftBox>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Box display="flex" alignItems="center">
                                        <Box flex={1}>
                                            <StatsCounterCard
                                                count={device.battery}
                                                suffix="%"
                                                title="Battery"
                                                transparent={true}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 2 }} />
                                        <Box flex={1}>
                                            <StatsCounterCard
                                                count={device.temperature}
                                                suffix={<>&deg;C</>}
                                                title="Temperature"
                                                transparent={true}
                                            />
                                        </Box>
                                    </Box>
                                    {showAdvancedDeviceLevel && (
                                        <>
                                            <Divider />
                                            <Box display="flex" alignItems="center">
                                                <Box flex={1}>
                                                    <StatsCounterCard
                                                        count={device.rssi}
                                                        suffix="-dB"
                                                        title="Signal Strength"
                                                        transparent={true}
                                                    />
                                                </Box>
                                                <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 2 }} />
                                                <Box flex={1}>
                                                    <StatsCounterCard
                                                        count={device.humidity}
                                                        suffix="%"
                                                        title="Humidity"
                                                        transparent={true}
                                                    />
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                        <SoftBox my={1}>
                            <Divider />
                        </SoftBox>
                        <DeviceStatsCard
                            icon={getConnectivityIcon(device, showAdvancedDeviceLevel)} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"}
                            title={{ text: getConnectivityIcon(device, showAdvancedDeviceLevel).text, subtext: device.organisation_id == "clearways" ? `Last Updated: ${device.last_updated}` : "", fontWeight: "medium" }}
                        />
                        <SoftBox my={1}>
                            <Divider />
                        </SoftBox>
                        <DeviceStatsCard
                            icon={getDeviceTypeIcon(device, showAdvancedDeviceLevel)} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"}
                            title={{ text: getDeviceTypeIcon(device, showAdvancedDeviceLevel).text, subtext: device.connected_state == "eco" ? `Last Updated: ${device_liveview_last_update}` : "", fontWeight: "medium" }}
                        />
                        {showAdvancedDeviceLevel && (
                            <>
                                <SoftBox my={1}>
                                    <Divider />
                                </SoftBox>
                                <DeviceStatsCard icon={{ component: "fingerprint", colour: "dark" }} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"} title={{ text: device?.serial_number || "N/A", fontWeight: "medium" }} />
                            </>
                        )}
                    </SoftBox>
                </Card>
            </SoftBox>
        </Card>
    );

    return overviewInfo;
}

DeviceOverviewTab.propTypes = {
    device: PropTypes.object
}

export default DeviceOverviewTab;